import { asset } from "./assetUrl";

/**
 * Recognising the placeholder logo, i.e. "this creator has not set one".
 *
 * ── Why this is needed at all ───────────────────────────────────────────────
 * The API does not send an empty logo when a creator has not set one — it
 * substitutes the platform default itself (MembershipBaseController's
 * DEFAULT_BRAND_LOGO, via mediaUrl()). So by the time the frontend sees
 * `community.logo` it is already a non-empty string, and a plain
 * `logo || DEFAULT` test never fires. Matching the known default URLs is what
 * lets this work without a bridge change — which matters, because that Lumen
 * app is being retired and nothing new should be added to it.
 *
 * ── What replaces it ────────────────────────────────────────────────────────
 * BrandDefaultMark, an INLINE svg. The old default was a hosted PNG with a
 * solid background — no alpha at all — so on a dark membership it rendered as
 * a bright slab in the corner of an otherwise dark header. A raster cannot
 * follow a theme; two rasters could, but still could not follow the CREATOR's
 * colour, because that differs per membership. The svg does both: the wordmark
 * takes `currentColor`, the ring and question mark take `var(--brand-primary)`.
 *
 * If a creator ever uploads an image that happens to live at one of these URLs,
 * they get the placeholder instead of a byte-identical copy of it. That is the
 * whole cost of not touching the bridge.
 */

/**
 * Paths, matched without scheme/host/query so a CDN move or a cache-busting
 * suffix does not silently turn the white box back on.
 */
const DEFAULT_PATHS = [
  "/images/logo.png", // membershipdata.flexifunnels.com — the old learner-header default
  "/flexicontest/flexilogo.svg", // img.flexifunnels.com — the old course-landing default
  // The admin app serves the placeholder itself and sends THAT as the default.
  // Still "no logo set": a baked-colour file would pin a membership to the
  // wrong ink — the original bug arriving by a new route.
  "/images/brand-default.svg",
  "/images/brand-default-light.png",
  "/images/brand-default-dark.png",
];

/**
 * The placeholder as a FILE, for the places that need a URL rather than an
 * element: an <img> in a course-landing template, the admin's Brand preview.
 * Its colours are baked, because `currentColor` and CSS variables cannot cross
 * into an <img>'s isolated document — see BrandDefaultMark.
 */
export const BRAND_DEFAULT_SVG = asset("/images/brand-default.svg");

/** Is this URL the platform placeholder rather than a creator's own logo? */
export function isPlatformDefaultLogo(url) {
  const raw = String(url || "").trim();
  if (raw === "") return true; // nothing set at all

  let path = raw;
  try {
    // Absolute URLs only — a bare path throws, and is already what we want.
    path = new URL(raw, "https://x.invalid").pathname;
  } catch (e) {
    /* keep the raw string */
  }

  return DEFAULT_PATHS.includes(path.toLowerCase());
}
